import { useState } from 'react';
import { Link } from 'react-router-dom';

import { RevenueTrend } from '../components/Charts.tsx';
import { DataTable } from '../components/DataTable.tsx';
import { ItemLabel } from '../components/ItemLabel.tsx';
import { Badge, Card, Empty, Notice, Spinner, StatTile } from '../components/Ui.tsx';
import { api, type SalesRangeKey, type SalesWindow, type TradeSide } from '../lib/api.ts';
import { useAsync } from '../lib/auth.tsx';
import { money, moneyShort, number, relativeTime, shortDate, unitMoney } from '../lib/format.ts';

type Sale = Awaited<ReturnType<typeof api.insights.sales>>['sales'][number];

/**
 * Raw sales, newest first.
 *
 * Every other page in this dashboard is built from public market data, which
 * says what a shop lists and holds but never what it sold. This page is the one
 * that needs the webhook, so when there is no webhook it says so plainly rather
 * than rendering an empty table that looks broken.
 */
type Grain = 'hour' | 'day' | 'week' | 'month';

const PRESETS: Array<{ key: Exclude<SalesRangeKey, 'custom'>; label: string }> = [
  { key: '24h', label: '24 hours' },
  { key: 'week', label: 'Week' },
  { key: 'month', label: 'Month' },
  { key: 'all', label: 'All time' },
];

/**
 * How a bucket label reads at each size.
 *
 * An hourly tick needs the clock and a monthly one needs the year; showing
 * "1 Mar" for both is what made the old chart ambiguous.
 */
function bucketLabel(bucket: string, grain: Grain): string {
  // Bucket keys are UTC. An hourly key arrives as `2026-09-03T14:00` with no
  // zone, and JavaScript reads a bare date-time as *local*, which would shift
  // every tick by the viewer's offset - so the Z is added back explicitly.
  const date = new Date(grain === 'hour' ? `${bucket}Z` : `${bucket}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return bucket;

  if (grain === 'hour') {
    return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  }
  if (grain === 'month') {
    return date.toLocaleDateString('en-GB', { month: 'short', year: '2-digit', timeZone: 'UTC' });
  }
  const day = date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' });
  return grain === 'week' ? `w/c ${day}` : day;
}

/** The window as a person would say it, for headings and stat labels. */
function windowLabel(range: SalesRangeKey, from: string, to: string): string {
  if (range === '24h') return 'last 24 hours';
  if (range === 'week') return 'last 7 days';
  if (range === 'month') return 'last 30 days';
  if (range === 'all') return 'all time';
  return `${shortDate(from)} to ${shortDate(to)}`;
}

const isoDay = (date: Date) => date.toISOString().slice(0, 10);

export function SalesPage() {
  // One window drives the whole page - the chart, the estimate table and the
  // transaction list. Two separate range controls left people unsure which
  // section a given control was changing.
  const [window_, setWindow] = useState<SalesWindow>({ range: 'month' });
  const [customFrom, setCustomFrom] = useState(() =>
    isoDay(new Date(Date.now() - 30 * 86_400_000)),
  );
  const [customTo, setCustomTo] = useState(() => isoDay(new Date()));

  // Which direction of trade is on screen. Kept separate from the range so
  // switching sides does not reset the period being examined.
  const [side, setSide] = useState<TradeSide>('sell');

  const key = `${JSON.stringify(window_)}|${side}`;
  const sales = useAsync(() => api.insights.sales(window_, side), [key]);
  const series = useAsync(() => api.insights.salesSeries(window_, side), [key]);

  const activePreset = 'range' in window_ ? window_.range : null;
  const applyCustom = () => setWindow({ from: customFrom, to: customTo });

  if (sales.loading) return <Spinner />;
  const data = sales.data;

  return (
    <>
      <header className="page-head">
        <div>
          <h1>Sales records</h1>
          <p>
            Every completed transaction on your connected account. This is the one view that cannot
            come from public market data - the server publishes what shops list and hold, never what
            they sold.
          </p>
        </div>
      </header>

      {/* Direction first, then period. Everything below reads both. */}
      <div className="range-bar">
        <div className="pill-row side-toggle">
          <button
            className={`pill ${side === 'sell' ? 'active' : ''}`}
            onClick={() => setSide('sell')}
          >
            You sold
          </button>
          <button
            className={`pill ${side === 'buy' ? 'active' : ''}`}
            onClick={() => setSide('buy')}
          >
            Customers sold to you
          </button>
        </div>

        <div className="pill-row">
          {PRESETS.map((preset) => (
            <button
              key={preset.key}
              className={`pill ${activePreset === preset.key ? 'active' : ''}`}
              onClick={() => setWindow({ range: preset.key })}
            >
              {preset.label}
            </button>
          ))}
        </div>

        <div className="range-custom">
          <label>
            <span className="small muted">From</span>
            <input
              type="date"
              value={customFrom}
              max={customTo}
              onChange={(event) => setCustomFrom(event.target.value)}
            />
          </label>
          <label>
            <span className="small muted">To</span>
            <input
              type="date"
              value={customTo}
              min={customFrom}
              max={isoDay(new Date())}
              onChange={(event) => setCustomTo(event.target.value)}
            />
          </label>
          <button
            className={activePreset == null ? 'primary' : ''}
            onClick={applyCustom}
            disabled={!customFrom || !customTo}
          >
            Apply
          </button>
        </div>

        {data && (
          <span className="range-note small muted">
            {windowLabel(data.range, data.from, data.to)}
            {data.earliest && data.range === 'all' && ` - since ${shortDate(data.earliest)}`}
          </span>
        )}
      </div>

      {!data?.hasWebhook && side === 'sell' && (
        <div style={{ marginBottom: 16 }}>
          <Notice tone="warn">
            <strong>No webhook connected, so there is no exact sales record.</strong> The public API
            never attributes a trade to a shop - its daily sales figures are server-wide totals. Who
            bought what, and when, only arrives through an account webhook you configure in game.{' '}
            <Link to="/connections">Set one up</Link>
            {(data?.estimatedTotals.items ?? 0) > 0 &&
              ' Meanwhile, the estimate below is derived from how your stock levels moved.'}
          </Notice>
        </div>
      )}

      {/* Trend first: the shape of trade over time is the question this page is
          opened to answer, and the tables below are the detail behind it.
          Units and revenue get their own axes rather than sharing one - a
          second y-scale on the same plot invites reading a crossover as
          meaningful when the scales are arbitrary. */}
      <Card
        title={side === 'sell' ? 'Sales over time' : 'Purchases over time'}
        subtitle={
          series.data?.source === 'webhook'
            ? 'Exact, from your account webhook'
            : series.data?.source === 'none'
              ? 'Needs a webhook - stock movement cannot see this side'
              : 'Estimated from stock movement - a lower bound'
        }
      >
        {series.loading ? (
          <Spinner />
        ) : series.data?.source === 'none' ? (
          <Empty title="This side needs a webhook">
            A customer selling into your chest and you refilling it are the same thing to a stock
            scan - both are stock going up. Rather than guess between them, we show nothing here
            until an account webhook can tell us which happened.{' '}
            <Link to="/connections">Connect one</Link>
          </Empty>
        ) : !series.data || series.data.totals.units === 0 ? (
          <Empty title="Nothing to plot yet">
            Nothing has been recorded or inferred in this window.
          </Empty>
        ) : (
          <div className="chart-pair">
            <div>
              <div className="chart-title">{side === 'sell' ? 'Revenue' : 'Spent'}</div>
              <RevenueTrend
                data={series.data.buckets}
                series={[{ key: 'revenue', label: 'Revenue', color: 'var(--series-1)' }]}
                height={240}
                formatBucket={(bucket) => bucketLabel(bucket, series.data!.granularity)}
              />
            </div>
            <div>
              <div className="chart-title">{side === 'sell' ? 'Units sold' : 'Units bought'}</div>
              <RevenueTrend
                data={series.data.buckets}
                series={[{ key: 'units', label: 'Units', color: 'var(--series-3)' }]}
                height={240}
                formatValue={(value) => number(value)}
                formatBucket={(bucket) => bucketLabel(bucket, series.data!.granularity)}
              />
            </div>
          </div>
        )}
        {series.data?.source === 'estimated' && series.data.totals.units > 0 && (
          <Notice>
            <strong>This is a lower bound.</strong> Anything sold and restocked between two scans
            is invisible to it, so the real curve sits above this one. A webhook makes it exact.
          </Notice>
        )}
      </Card>

      {data && side === 'sell' && data.estimatedTotals.items > 0 && (
        <>
          <div className="grid tiles">
            <StatTile
              label={`Estimated sold, ${windowLabel(data.range, data.from, data.to)}`}
              value={number(data.estimatedTotals.units)}
              foot={<span className="muted">units, from stock movement</span>}
            />
            <StatTile
              label="Estimated revenue"
              value={moneyShort(data.estimatedTotals.revenue)}
              foot={<span className="muted">at the price recorded each time</span>}
            />
            <StatTile
              label="Items moving"
              value={number(data.estimatedTotals.items)}
              foot={<span className="muted">of the items you list</span>}
            />
          </div>

          <Card
            title="Estimated sales, from stock movement"
            subtitle="A drop in a chest between two scans is units that left it"
          >
            <DataTable
              rows={data.estimated}
              rowKey={(row) => row.itemKey}
              initialSort={{ key: 'revenue', dir: 'desc' }}
              searchPlaceholder="Search items"
              pageSize={50}
              columns={[
                {
                  key: 'item',
                  header: 'Item',
                  searchText: (row) => `${row.displayName} ${row.material}`,
                  sortValue: (row) => row.displayName,
                  render: (row) => (
                    <ItemLabel
                      itemKey={row.itemKey}
                      material={row.material}
                      displayName={row.displayName}
                      size={20}
                    />
                  ),
                },
                {
                  key: 'units',
                  header: 'Units sold',
                  align: 'num',
                  sortValue: (row) => row.unitsSold,
                  render: (row) => <strong>{number(row.unitsSold)}</strong>,
                },
                {
                  key: 'revenue',
                  header: 'Est. revenue',
                  align: 'num',
                  sortValue: (row) => row.estimatedRevenue,
                  render: (row) => money(row.estimatedRevenue),
                },
                {
                  key: 'confidence',
                  header: 'Based on',
                  align: 'num',
                  sortValue: (row) => row.observations,
                  render: (row) => (
                    <span
                      title={
                        row.restocks > 0
                          ? `${row.restocks} restock(s) happened in this window, so sales before each one are invisible - the real figure is higher.`
                          : 'No restock seen, so this is close to the full picture for the window.'
                      }
                    >
                      {number(row.observations)} scans
                      {row.restocks > 0 && (
                        <div className="small muted">{row.restocks} restocks</div>
                      )}
                    </span>
                  ),
                },
                {
                  key: 'last',
                  header: 'Last movement',
                  align: 'num',
                  sortValue: (row) => row.lastSeen,
                  render: (row) => relativeTime(row.lastSeen),
                },
              ]}
            />
            <div style={{ marginTop: 12 }}>
              <Notice>
                <strong>This is a lower bound, not a record.</strong> Anything sold and restocked
                between two scans is invisible, and the server rescans most shops infrequently, so
                the true figure is higher. A webhook gives the exact record.
              </Notice>
            </div>
          </Card>
        </>
      )}

      {data?.totals && data.totals.transactions > 0 && (
        <div className="grid tiles">
          <StatTile
            label={`${side === 'sell' ? 'Revenue' : 'Spent'}, ${windowLabel(data.range, data.from, data.to)}`}
            value={moneyShort(data.totals.revenue)}
          />
          <StatTile
            label={side === 'sell' ? 'Units sold' : 'Units bought'}
            value={number(data.totals.units)}
          />
          <StatTile label="Transactions" value={number(data.totals.transactions)} />
          <StatTile
            label="Records held"
            value={number(data.total)}
            foot={
              data.returned < data.total ? (
                <span className="muted">showing the newest {number(data.returned)}</span>
              ) : undefined
            }
          />
        </div>
      )}

      <Card title="Transactions">
        {!data || data.sales.length === 0 ? (
          <Empty title="Nothing recorded in this window">
            <span className="muted">
              {data?.hasWebhook
                ? 'Your webhook is connected but no sales have come through in this period.'
                : 'Sales appear here once an account webhook is delivering events.'}
            </span>
          </Empty>
        ) : (
          <DataTable
            rows={data.sales}
            rowKey={(sale) => sale.id}
            initialSort={{ key: 'when', dir: 'desc' }}
            searchPlaceholder="Search item, buyer or shop"
            pageSize={100}
            columns={buildColumns()}
          />
        )}
      </Card>
    </>
  );
}

function buildColumns() {
  return [
    {
      key: 'when',
      header: 'When',
      align: 'num' as const,
      sortValue: (sale: Sale) => sale.occurredAt,
      render: (sale: Sale) => (
        <span title={new Date(sale.occurredAt).toLocaleString()}>
          {relativeTime(sale.occurredAt)}
        </span>
      ),
    },
    {
      key: 'item',
      header: 'Item',
      searchText: (sale: Sale) => `${sale.displayName ?? ''} ${sale.itemCode ?? ''}`,
      sortValue: (sale: Sale) => sale.displayName,
      render: (sale: Sale) =>
        sale.itemCode == null ? (
          // A money movement with no item attached is normal: the API's
          // transaction record carries no item fields at all.
          <span className="muted">
            not itemised
            {sale.rawType ? <div className="small">{sale.rawType}</div> : null}
          </span>
        ) : (
          <ItemLabel
            itemKey={sale.itemCode}
            material={sale.itemCode}
            displayName={sale.displayName ?? sale.itemCode}
            size={20}
          />
        ),
    },
    {
      key: 'side',
      header: 'Direction',
      sortValue: (sale: Sale) => sale.side,
      render: (sale: Sale) => (
        <Badge tone={sale.side === 'sell' ? 'good' : 'muted'}>
          {sale.side === 'sell' ? 'sold' : 'bought'}
        </Badge>
      ),
    },
    {
      key: 'quantity',
      header: 'Qty',
      align: 'num' as const,
      sortValue: (sale: Sale) => sale.quantity,
      render: (sale: Sale) => number(sale.quantity),
    },
    {
      key: 'unitPrice',
      header: 'Unit price',
      align: 'num' as const,
      sortValue: (sale: Sale) => sale.unitPrice,
      render: (sale: Sale) => unitMoney(sale.unitPrice),
    },
    {
      key: 'total',
      header: 'Total',
      align: 'num' as const,
      sortValue: (sale: Sale) => sale.totalAmount,
      render: (sale: Sale) => <strong>{money(sale.totalAmount)}</strong>,
    },
    {
      key: 'counterparty',
      header: 'Buyer',
      searchText: (sale: Sale) => sale.counterparty ?? '',
      sortValue: (sale: Sale) => sale.counterparty,
      render: (sale: Sale) => sale.counterparty ?? <span className="muted">-</span>,
    },
    {
      key: 'shop',
      header: 'Shop',
      searchText: (sale: Sale) => sale.shopName ?? '',
      sortValue: (sale: Sale) => sale.shopName,
      render: (sale: Sale) => (
        <span className="muted small">{sale.shopName ?? 'unassigned'}</span>
      ),
    },
  ];
}
